import { Injectable } from '@angular/core';
import { HostApiService } from 'lib-host-interface';
import { BehaviorSubject } from 'rxjs';
import { AssociationProfile, ModUserProfile } from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { USER_TYPES } from 'projects/app-nmls-entity-profile/src/app/models/profiles.enum';
import { AppHostUserInfo } from '../model/user.model';
import { environment } from '../../environments/environment';

export interface FilingStatusDisplayRules {
  filingStatus?: FILING_STATUS;
  canDelete?: boolean;
  canRecall?: boolean;
  canSubmit?: boolean;
  canRequestAttestation?: boolean;
  canAttest?: boolean;
  readOnlyMode?: boolean;
  displayedStatus?: string;
  isShowDisplayStatus?: boolean;
}

export interface CanAttestResponse {
  attestChecked: boolean;
  employmentHistoryReviewed?: boolean;
  hasError: boolean;
  isCompletenessCheckLoading: boolean;
  errorCount?: number;
}

export enum FILING_STATUS {
  UNSUBMITTED = 'Unsubmitted',
  ATTESTATION_REQUESTED = 'Attestation Requested',
  ATTESTED = 'Attested',
  SUBMITTED = 'Submitted',
  PROCESSED = 'Filing Processed',
  FAILED = 'Failed Processing',
}

interface AuthTarget {
  entityId: number;
  subjectId: number;
}

type CompareModToDecafAssociations = {
  [key in USER_TYPES]?: number[];
};

interface UserPermissions {
  isCompanyUser: boolean;
  isCreatedByEntity: boolean;
  isSubjectOfFiling: boolean;
  hasConfidentialInfoPermissions: boolean;
  hasAdministratorPermissions: boolean;
  hasMU3Access: boolean;
  hasMU3SubmitterAccess: boolean;
}

interface FilingStatusMatrix {
  [FILING_STATUS.UNSUBMITTED]: FilingStatusDisplayRules;
  [FILING_STATUS.ATTESTATION_REQUESTED]: FilingStatusDisplayRules;
  [FILING_STATUS.ATTESTED]: FilingStatusDisplayRules;
  [FILING_STATUS.SUBMITTED]: FilingStatusDisplayRules;
  [FILING_STATUS.PROCESSED]: FilingStatusDisplayRules;
  [FILING_STATUS.FAILED]: FilingStatusDisplayRules;
}

@Injectable({
  providedIn: 'root',
})
export class DisplayRulesService {
  hostApi: HostApiService;
  displayRulesSubj = new BehaviorSubject<FilingStatusDisplayRules>({});
  appHostUserInfoSub = new BehaviorSubject<AppHostUserInfo>({});
  displayRules$ = this.displayRulesSubj.asObservable();
  appHostUserInfo: AppHostUserInfo;
  associationIds: CompareModToDecafAssociations;
  // MU3 is Company-only (guard rejects non-Company users at the route level)
  userPermissions: UserPermissions = {
    isCompanyUser: false,
    isCreatedByEntity: false,
    isSubjectOfFiling: false,
    hasConfidentialInfoPermissions: false,
    hasAdministratorPermissions: false,
    hasMU3Access: false,
    hasMU3SubmitterAccess: false,
  };
  filingStatus: FilingStatusDisplayRules;
  canAttestResp: any = new BehaviorSubject<any>({
    attestChecked: false,
    hasError: false,
    isCompletenessCheckLoading: true,
  });
  canAttestResp$ = this.canAttestResp.asObservable();
  profileData: ModUserProfile;
  // MU3 is Company-only — isRegulatorUser is always false (kept for service interface compatibility)
  isRegulatorUser = new BehaviorSubject(false);
  navigationError = new BehaviorSubject(false);

  constructor() {}

  getCurrentDisplayRules(): FilingStatusDisplayRules {
    return this.displayRulesSubj.getValue();
  }

  sentIsVerifiedData(data: boolean) {
    this.canAttestResp.next({
      ...this.canAttestResp.getValue(),
      attestChecked: data,
    });
  }

  setEmploymentHistoryReviewedData(data: boolean) {
    this.canAttestResp.next({
      ...this.canAttestResp.getValue(),
      employmentHistoryReviewed: data,
    });
  }

  setUserPermissions(
    profile: ModUserProfile,
    userInfo: AppHostUserInfo,
    role: string,
    permissions: string[],
    metadata?,
  ) {
    this.userPermissions.isCompanyUser = role === 'Company';
    this.appHostUserInfo = userInfo;
    if (permissions.includes('ConfidentialInfo')) {
      this.userPermissions.hasConfidentialInfoPermissions = true;
    } else if (permissions.includes('Administrator')) {
      this.userPermissions.hasAdministratorPermissions = true;
    }
    this.userPermissions.hasMU3Access = permissions.includes('MU3');
    this.userPermissions.hasMU3SubmitterAccess = permissions.includes('MU3Submitter');

    // index by userType and create list of entityIds
    this.profileData = profile;
    const associationIds = profile?.associations?.reduce((hashmap, { userType, entityId }: AssociationProfile) => {
      hashmap[userType] ? hashmap[userType].push(entityId) : (hashmap[userType] = [entityId]);
      return hashmap;
    }, {});
    this.associationIds = associationIds;

    const filingStatus =
      metadata?.attributes?.filingStatus?.current ?? this.deriveFilingStatus(metadata?.status, metadata?.resultStatus);
    const authTarget = metadata?.target;
    this.setDisplayRulesByFilingStatus(filingStatus, authTarget, metadata);
  }

  setDisplayRulesByFilingStatus(filingStatus: FILING_STATUS, authTarget: AuthTarget, metadata?) {
    if (!authTarget) return;

    const { entityId, subjectId } = authTarget;

    this.userPermissions.isCreatedByEntity = this.associationIds?.Company?.some((id) => id === entityId);
    this.userPermissions.isSubjectOfFiling = this.associationIds?.Individual?.some((id) => id === subjectId);
    this.displayRulesSubj.next(this.getDisplayRulesByFilingStatus(filingStatus, this.userPermissions, metadata));
  }

  getDisplayRulesByFilingStatus(status: FILING_STATUS, userPermissions: UserPermissions, metadata?) {
    if (!userPermissions) return;

    const { isCompanyUser, isCreatedByEntity } = userPermissions;
    const isAdmin = userPermissions.hasAdministratorPermissions;
    const companyIsAuthorized = (isCompanyUser && isCreatedByEntity) || isAdmin;
    const canSubmitFiling = companyIsAuthorized && (userPermissions.hasMU3SubmitterAccess || isAdmin);
    const canDeleteFiling =
      isCompanyUser && (userPermissions.hasMU3Access || userPermissions.hasMU3SubmitterAccess || isAdmin);
    const { orgClass } = metadata?.userInfo?.createdBy ?? '';
    const { displayedStatus, isShowDisplayStatus } = this.getDisplayedStatus(orgClass, status, isCompanyUser);
    const isNotCurrentVersion = !!(
      metadata?.templateVersion && metadata.templateVersion !== environment.currentVersion
    );
    const ruleMap: FilingStatusMatrix = {
      [FILING_STATUS.UNSUBMITTED]: {
        canDelete: canDeleteFiling,
        canRequestAttestation: companyIsAuthorized,
        canSubmit: canSubmitFiling,
        readOnlyMode: isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.ATTESTATION_REQUESTED]: {
        canDelete: canDeleteFiling,
        canRecall: companyIsAuthorized,
        readOnlyMode: companyIsAuthorized || isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.ATTESTED]: {
        filingStatus: FILING_STATUS.ATTESTED,
        canDelete: canDeleteFiling,
        canRecall: companyIsAuthorized,
        canSubmit: canSubmitFiling,
        readOnlyMode: companyIsAuthorized || isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.SUBMITTED]: {
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: true,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.PROCESSED]: {
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: true,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.FAILED]: {
        canDelete: canDeleteFiling,
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: true,
        displayedStatus: displayedStatus,
      },
    };

    this.filingStatus = ruleMap[status];

    return ruleMap[status];
  }

  getDisplayedStatus(orgClass, filingStatus, isCompanyUser) {
    if (filingStatus === FILING_STATUS.UNSUBMITTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Draft', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTATION_REQUESTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Attestation Requested', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Attested', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.SUBMITTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Submitted', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.PROCESSED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Filing Processed', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.FAILED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Failed Processing', isShowDisplayStatus: true };
    } else {
      return { displayedStatus: '', isShowDisplayStatus: false };
    }
  }

  // Derives the filing status from DECAF instance-level status and resultStatus
  // when attributes.filingStatus.current is not populated.
  // Note: Unlike nmlsmod-utils/filingStatusUtil which maps SUBMITTED → PROCESSING,
  // MU3 maps directly to SUBMITTED since the display rules don't have a PROCESSING state.
  private deriveFilingStatus(status: string | undefined, resultStatus?: string): FILING_STATUS {
    if (resultStatus === 'Filing Processed') return FILING_STATUS.PROCESSED;
    if (resultStatus === 'Failed Processing') return FILING_STATUS.FAILED;

    switch (status) {
      case 'DRAFT':
        return FILING_STATUS.UNSUBMITTED;
      case 'SUBMITTED':
        return FILING_STATUS.SUBMITTED;
      default:
        return FILING_STATUS.UNSUBMITTED;
    }
  }
}
